import { Component, inject, OnInit, OnDestroy, signal, computed, effect } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, finalize, map, Observable, of, Subject, takeUntil, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DynamicFilterConfig, DynamicFilterValues, EnumValues, FilterOption, createDynamicFilterState } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { PaginatorState } from 'primeng/paginator';
import { EnumService } from '../../../services/enum.service';
import { ProductionWorkOrder } from '../../../models/production/production-work-order';
import { Requisition, RequisitionSearchCommand } from '../../../models/requisitions/requisition';
import { UserGroup } from '../../../models/authorization/enum/user-group.enum';
import { ProductionWorkOrderProxyService } from '../../../proxies/production/production-work-order.proxy.service';
import { RequisitionProxyService } from '../../../proxies/inventory/requisition.proxy.service';

const ENTITY_DEF_PRODUCTION_WORK_ORDER = 40;

@Component({
  selector: 'app-requisitions',
  templateUrl: './requisitions.component.html',
  styleUrl: './requisitions.component.scss',
})
export class RequisitionsComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private enumService = inject(EnumService);
  private translateService = inject(TranslateService);
  private requisitionProxyService = inject(RequisitionProxyService);
  private pwoProxyService = inject(ProductionWorkOrderProxyService);

  private destroy$ = new Subject<void>();
  private appliedFilters: DynamicFilterValues = {};

  isLoading = signal<number>(0);
  showFilters = signal<boolean>(true);
  requisitions = signal<Requisition[]>([]);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'requisitions-filters', config: this.filterConfig });

  requisitionStatus = signal<EnumValues>({});
  requisitionType = signal<EnumValues>({});
  enumsReady = signal(false);

  listPWOs = signal<ProductionWorkOrder[]>([]);
  filteredPWOs = signal<ProductionWorkOrder[]>([]);

  currentPage = 1;
  pageSize = 25;
  totalRecords = 0;

  statusOptions = computed<FilterOption[]>(() => {
    const statuses = this.requisitionStatus();
    return Object.keys(statuses)
      .filter((key) => !isNaN(Number(key)))
      .map((key) => ({
        value: Number(key),
        label: statuses[Number(key)],
      }));
  });

  canEdit = computed(() => {
    return AuthService.hasRole(UserGroup.RequisitionEditor) || AuthService.hasRole(UserGroup.RequisitionAdmin);
  });

  pwosMap: Record<string, ProductionWorkOrder> = {};

  getRowData(rowData: unknown): Requisition {
    return rowData as Requisition;
  }

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters));

    effect(
      () => {
        const statusesValue = this.enumService.getEnum('RequisitionStatus', 'inventory').values;
        if (Object.keys(statusesValue).length > 0) {
          this.requisitionStatus.set(statusesValue);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        const typesValue = this.enumService.getEnum('RequisitionType', 'inventory').values;
        if (Object.keys(typesValue).length > 0) {
          this.requisitionType.set(typesValue);
          this.enumsReady.set(true);
        }
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        if (this.translateService.currentLang && this.enumsReady()) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.loadInitialData();

    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.tryInitializeFilterConfig();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadInitialData(): void {
    this.startLoading();
    this.pwoProxyService
      .search({ status: [1, 2] }, 1, 100)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (result) => {
          const pwos = result?.data?.items || [];
          this.listPWOs.set(pwos);
          this.filteredPWOs.set(pwos);
          this.pwosMap = pwos.reduce(
            (acc, pwo) => {
              acc[pwo.id] = pwo;
              return acc;
            },
            {} as Record<string, ProductionWorkOrder>,
          );
        },
      });
  }

  private tryInitializeFilterConfig(): void {
    if (this.translateService.currentLang && this.enumsReady()) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'requisitionNumber',
          label: translate('Requisition_Number', 'Requisition Number'),
          placeholder: translate('Enter_requisition_number', 'Enter requisition number'),
          defaultVisible: true,
        },
        {
          type: 'autocomplete',
          formControlName: 'productionWorkOrder',
          label: translate('Production_Work_Order', 'Production Work Order'),
          placeholder: translate('Select_production_work_order', 'Select PWO'),
          autocompleteConfig: {
            suggestions: this.filteredPWOs(),
            optionLabel: 'number',
            optionValue: 'id',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.filterPWOs(event),
            resolveById: (id: string) => this.resolveProductionWorkOrder(id),
            dropdown: true,
          },
          showClear: true,
          defaultVisible: false,
        },
        {
          type: 'multiselect',
          formControlName: 'statuses',
          label: translate('Status', 'Status'),
          placeholder: translate('Select_status', 'Select status'),
          options: this.statusOptions(),
          showClear: true,
          defaultVisible: false,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  private resolveProductionWorkOrder(id: string): Observable<ProductionWorkOrder | null> {
    return this.pwoProxyService.getById(id).pipe(
      map((response) => response.data ?? null),
      catchError((error: HttpErrorResponse) => (error.status === 404 ? of(null) : throwError(() => error))),
    );
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  search(): void {
    const formValues = this.appliedFilters;
    const requisitionNumber = (formValues['requisitionNumber'] as string | undefined)?.trim();
    const productionWorkOrder = formValues['productionWorkOrder'] as string | { id?: string } | null | undefined;
    const pwoId = typeof productionWorkOrder === 'string' ? productionWorkOrder : productionWorkOrder?.id;

    const statusesRaw = formValues['statuses'];
    const statuses: number[] = Array.isArray(statusesRaw)
      ? statusesRaw.map((s: { value?: number }) => (typeof s === 'object' && s.value !== undefined ? s.value : s) as number)
      : [];

    const command: RequisitionSearchCommand = {
      requisitionNumber: requisitionNumber || undefined,
      initiator: pwoId ? { entityDefinitionCode: ENTITY_DEF_PRODUCTION_WORK_ORDER, reference: pwoId } : undefined,
      statuses: statuses.length > 0 ? statuses : undefined,
    };

    this.startLoading();
    this.requisitionProxyService
      .search(command, this.currentPage, this.pageSize)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (result) => {
          this.requisitions.set(result?.data?.items || []);
          this.totalRecords = result?.data?.totalCount || 0;
        },
      });
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage = (event.page || 0) + 1;
    this.pageSize = event.rows || this.pageSize;
    this.search();
  }

  filterPWOs(event: AutoCompleteCompleteEvent): void {
    const query = event.query.toLowerCase();
    const filtered = this.listPWOs().filter((pwo) => pwo.number?.toLowerCase().includes(query));
    this.filteredPWOs.set(filtered);
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    this.appliedFilters = filters;
    this.currentPage = 1;
    this.search();
  }

  redirectToDetail(id?: string): void {
    const queryParams = id ? { id } : { id: 'new' };
    this.router.navigate(['/requisition-details'], { queryParams });
  }
}
